import angular from 'angular';
import ngAnimate from 'angular-animate';
import CSMBooksWeb from '../../../CSMBooksWeb';
import CSMCommonWeb from 'csm-common-web';
import menu from '../../menu/Menu';
import purchaseOrders from '../../checks/purchaseOrders/purchaseOrders';
import books from '../../books';
import { bulkItemsController } from './bulkItems';
import { bulkPickupsController } from './bulkPickups';
import { bulkPurchaseOrderController } from './bulkPurchaseOrders';
import { bulkStudentsController } from './bulkStudents';
import { bulkTagsController } from './bulkTags';
import { bulkVendorsController } from './bulkVendors';

export default angular
  .module('bulkActions', [
    menu,
    'ui.bootstrap',
    CSMBooksWeb,
    CSMCommonWeb,
    'ui.grid',
    'ui.grid.autoResize',
    'ui.grid.resizeColumns',
    'ui.grid.selection',
    ngAnimate,
    purchaseOrders,
    books
  ])
  /*******************************************************************************
   * Controller: Accordion
   *
   * TODO-CK Possibly change this to use the routes instead of accordions. If other
   * marked TODOs are changed, the accordion won't be needed, and can use routes
   * instead.
   *******************************************************************************/
  .controller('Accordion', AccordionController)
  .controller('GridItems', bulkItemsController)
  .controller('GridOpenOrders', bulkPickupsController)
  .controller('GridPurchaseOrders', bulkPurchaseOrderController)
  .controller('GridStudents', bulkStudentsController)
  .controller('GridTags', bulkTagsController)
  .controller('GridVendors', bulkVendorsController).name;

/*******************************************************************************
 * AccordionController */
/**
 * Controller for the bulk actions accordion.
 *******************************************************************************/
/* @ngInject */ function AccordionController(
  $q,
  $scope,
  $timeout,
  BooksSvc,
  BusyScreen,
  SERVER,
  KEYCODES,
  Alert
) {
  /*****************************************************************************
   * Constants
   *****************************************************************************/
  var TT_INDICATOR = SERVER.BooksDBConstants.TT_INDICATOR;
  var TT_MEMBERSHIP = SERVER.BooksDBConstants.TT_MEMBERSHIP;

  /**
   * Item types whose Effective Date can be changed. Mirrors the isSubItemType
   * eligibility used by the AddChangeItemDialog. getProductList() with a null
   * inventoryOnly filter returns 5 item types: the 3 included here plus IT_DONATION
   * and IT_GROUP. Kits fall under IT_GROUP, which we do not allow to have their
   * effective date changed. IT_DONATION is not a sub-item type, so it is excluded
   * in WNR as well.
   */
  const EFFECTIVE_DATE_ITEM_TYPES = [
    SERVER.BooksDBConstants.IT_FEES,
    SERVER.BooksDBConstants.IT_MEMBERSHIP,
    SERVER.BooksDBConstants.IT_PRODUCT
  ];

  initScope();
  loadData();

  /**
   * Filters a list of product items down to the types that are allowed to have
   * their Effective Date changed. Shared with bulkItems.js via scope inheritance.
   *
   * @param {Array} items
   * @returns {Array}
   */
  function filterEffectiveDateItems(items) {
    return items.filter((item) =>
      EFFECTIVE_DATE_ITEM_TYPES.includes(item.type)
    );
  }

  function getActiveVendors() {
    return BooksSvc.getVendorList(true, true).then((resp) => {
      $scope.dbVendors = resp.data.sortOn('fullName');
    });
  }

  /*****************************************************************************
   * getProductList */
  /**
   * Loads the list of products.
   *****************************************************************************/
  function getProductList() {
    return BooksSvc.getProductList(null, null, null, null).then(
      function (resp) {
        $scope.dbItems = filterEffectiveDateItems(resp.data).sortOn(
          'category',
          'name'
        );
      }
    );
  }

  /**
   * Used by bulkPickups.js gets a list of all Receipts that have pickups that have not been fulfilled.
   */
  function getOpenOrders() {
    return BooksSvc.viewOpenOrders().then((resp) => {
      $scope.dbOpenOrders = resp.data.sortOn('receiptNumber');
    });
  }

  /**
   * Used by bulkPurchaseOrders.js gets a list of all Open purchase orders for an organization.
   */
  function getOpenPurchaseOrders() {
    return BooksSvc.getOpenPurchaseOrders().then((resp) => {
      $scope.dbPurchaseOrders = resp.data.sortOn('poNumber');
    });
  }

  /*****************************************************************************
   * getStudentList */
  /**
   * Loads the list of students.
   *****************************************************************************/
  function getStudentList() {
    return BooksSvc.getBulkStudents().then(function (resp) {
      $scope.dbStudents = resp.data.sortOn('name');
    });
  }

  /*****************************************************************************
   * getTagList */
  /**
   * Get the list of tags.
   *****************************************************************************/
  function getTagList() {
    return BooksSvc.getTagList([TT_INDICATOR, TT_MEMBERSHIP].join(',')).then(
      function (resp) {
        $scope.dbTags = resp.data.membershipTags
          .concat(resp.data.indicatorTags)
          .sortOn('display');
      }
    );
  }

  /*****************************************************************************
   * initScope */
  /**
   * Sets scope properties and functions.
   *****************************************************************************/
  function initScope() {
    /** Options for the Item ui-grid. */ $scope.itemGridOptions = {};
    /** Options for the Tag ui-grid. */ $scope.tagGridOptions = {};
    /** Options for the Student ui-grid. */ $scope.studentGridOptions = {};
    /** Options for the Purchase Order ui-grid. */ $scope.purchaseOrderGridOptions =
      {};
    /** Options for the Vendors ui-grid. */ $scope.vendorOptions = {};
    /** Options for the Open Orders ui-grid. */ $scope.openOrdersOptions = {};
    /** Open only one Accordion at a time. */ $scope.oneAtATime = true;
    $scope.props = {
      // Sets the item effective dates accordion to be open by default.
      itemsOpen: true,
      // Used by bulkItems.js to set the effective date for the items.
      effectiveDate: null,
      // Used by bulkTags.js to set the after and before dates for the tags.
      assignedAfter: null,
      assignedBefore: null
    };

    $scope.onKeypress = onKeypress;
    $scope.filterEffectiveDateItems = filterEffectiveDateItems;
  }

  /*****************************************************************************
   * loadData */
  /**
   * Loads the items and tags for the organizations. TODO-CK Move the get
   * functions into each individual controller.
   *****************************************************************************/
  function loadData() {
    BusyScreen.setBusy(true);

    $q.all([
      getProductList(),
      getStudentList(),
      getTagList(),
      getOpenPurchaseOrders(),
      getActiveVendors(),
      getOpenOrders()
    ]).then(function () {
      BusyScreen.setBusy(false);
    });
  }

  /**
   * Fires whenever a row is selected or deselected.
   *
   * Used by each of the bulk action modules to verify that the number of selected rows does not exceed the limit. We set this up
   * because we found our system could not handle large amounts of data being processed at once. This is a way to limit the amount
   * of data being processed at once.
   *
   * @param {number} selectedRowsLength
   * @param {GridRow} row
   * @param {string} alertTitle
   * @param {number} limit
   */
  $scope.verifySingleSelect = function (
    selectedRowsLength,
    row,
    alertTitle,
    limit
  ) {
    /**
     * If the row is not selected then we do not need to enforce any rules about how many that are being
     * selected. We only need to enforce the rules when the row is selected.
     */
    if (!row.isSelected) return;

    if (selectedRowsLength >= limit) {
      Alert.info(
        `You have already selected the maximum number of items to be processed. The limit is (${limit}).`,
        alertTitle,
        true
      );

      row.setSelected(false);
    }
  };

  /**
   * Fires whenever the user shift selects/deselects or toggles all the rows.
   *
   * Used by each of the bulk action modules to verify that the number of selected rows does not exceed the limit. We set this up
   * because we found our system could not handle large amounts of data being processed at once. This is a way to limit the amount
   * of data being processed at once.
   *
   * @param {number} selectedRowsLength
   * @param {GridRow} row
   * @param {string} alertTitle
   * @param {number} limit
   */
  $scope.verifyMultiSelect = function (
    selectedRowsLength,
    rows,
    alertTitle,
    limit
  ) {
    /**
     * We check the selectedRows because this hook fires whenever a batch action occurs whether or not it is selecting or deselecting.
     */
    if (selectedRowsLength > limit) {
      Alert.info(
        `You’re selection was reduced down to the limit of ${limit} items. Please process these first before doing the rest.`,
        alertTitle,
        true
      );

      // Filter out all the rows that are in the 1st {limit} rows, then deselect all others. Priority is given to the 1st {limit} rows.
      rows
        .filter((row, index) => index >= limit)
        .forEach((row) => row.setSelected(false));
    }
  };

  /*****************************************************************************
   * onKeypress */
  /**
   * Checks that the SPACE key was pressed on the accordion headers in order to
   * simulate a click event.
   *****************************************************************************/
  function onKeypress(event) {
    var key = window.event ? event.keyCode : event.which;
    if (key == KEYCODES.SPACE)
      $timeout(function () {
        event.target.getElementsByClassName('panel-heading')[0].click();
      });
  }
}
